<template>
  <div v-if="historyFacts.length" class="evidence-box evidence-history mb-3 pt-3 border-t-subtle">
    <div class="evidence-box-title d-flex align-center mb-1">
      <v-icon size="16" color="#2563eb" class="mr-1">mdi-history</v-icon>
      <span class="section-sub-title">{{ isStationOffline ? '近 30 天整站离线历史记录统计' : '近 30 天设备离线历史记录统计' }}</span>
    </div>

    <!-- 统计结论陈述点 -->
    <div class="history-list pl-5">
      <div
        v-for="(hFact, hIdx) in historyFacts"
        :key="hIdx"
        class="history-item"
      >
        <span class="history-dot"></span>
        <span class="history-text">{{ hFact }}</span>
      </div>
    </div>

    <!-- 近 30 天历史记录明细表（默认合住，可点击展开查看） -->
    <div class="embedded-table-section mt-3 ml-5">
      <div class="table-toggle-bar" @click="isOpen = !isOpen">
        <div class="toggle-bar-left">
          <v-icon size="14" color="#2563eb" class="mr-1">
            {{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
          </v-icon>
          <span class="toggle-title">{{ isStationOffline ? `近 30 天整站离线历史记录明细 (共 ${historyList.length} 条)` : `近 30 天单机离线历史记录明细 (共 ${historyList.length} 条)` }}</span>
        </div>
        <span class="toggle-btn-label">
          {{ isOpen ? '收起表格' : '展开查看明细' }}
        </span>
      </div>

      <div v-show="isOpen" class="table-content-panel">
        <div v-if="historyList.length" class="table-scroll-wrap">
          <table class="modern-mini-table">
            <thead>
              <tr>
                <th style="width: 42px;">#</th>
                <th>设备名称</th>
                <th>告警码</th>
                <th>发生时间</th>
                <th>恢复时间</th>
                <th>离线历时</th>
                <th style="width: 86px;">状态</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in historyList"
                :key="idx"
                :class="{ 'highlight-target-row': isTargetDevice(row) }"
              >
                <td>{{ idx + 1 }}</td>
                <td>
                  <div class="dev-name-cell">
                    <span class="font-weight-medium">{{ row.equipmentName || '--' }}</span>
                    <span v-if="isTargetDevice(row) && !isStationOffline" class="target-badge">本设备</span>
                  </div>
                </td>
                <td class="code-font">{{ row.alarmCode || row.code || '--' }}</td>
                <td>{{ row.startTime ? row.startTime.slice(0, 19) : '--' }}</td>
                <td>{{ row.endTime && row.endTime !== '/' && row.endTime !== '--' ? row.endTime.slice(0, 19) : '至今未恢复' }}</td>
                <td>{{ formatDuration(row.startTime, row.endTime) }}</td>
                <td>
                  <span v-if="row.endTime && row.endTime !== '/' && row.endTime !== '--'" class="status-pill pill-recovered">已恢复</span>
                  <span v-else class="status-pill pill-active">未恢复</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="table-empty-wrap">
          <v-icon size="15" color="#bfbfbf" class="mr-1">mdi-information-outline</v-icon>
          <span>{{ isStationOffline ? '近 30 天未检测到该电站整站通信离线历史告警记录' : '近 30 天未检测到该电站单机离线历史告警记录' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import dayjs from 'dayjs'

const props = defineProps({
  historyFacts: {
    type: Array,
    default: () => []
  },
  historyList: {
    type: Array,
    default: () => []
  },
  alarmRecord: {
    type: Object,
    default: () => ({})
  }
})

const isStationOffline = computed(() => {
  const code = String(props.alarmRecord?.alarmCode || props.alarmRecord?.code || '').trim()
  const name = String(props.alarmRecord?.description || props.alarmRecord?.alarmName || '').trim()
  const dev = String(props.alarmRecord?.equipmentName || props.alarmRecord?.deviceName || '').trim()
  return code === 'A0_00_04_0001' || name.includes('整站') || !dev || dev === '--'
})

const isOpen = ref(false) // 默认合住

function isTargetDevice(row) {
  if (!row) return false
  const targetUuid = String(props.alarmRecord?.uuid || props.alarmRecord?.deviceVid || props.alarmRecord?.equipmentUUID || '').trim()
  const targetName = String(props.alarmRecord?.equipmentName || props.alarmRecord?.deviceName || '').trim()
  const rowUuid = String(row.uuid || row.deviceVid || row.equipmentUUID || '').trim()
  const rowName = String(row.equipmentName || row.deviceName || '').trim()
  return (targetUuid && rowUuid && targetUuid === rowUuid) || (targetName && rowName && targetName === rowName)
}

function formatDuration(start, end) {
  if (!start) return '--'
  if (!end || end === '/' || end === '--') {
    const dStart = dayjs(start)
    if (!dStart.isValid()) return '--'
    const diffMin = Math.round(dayjs().diff(dStart, 'minute'))
    if (diffMin < 60) return `${diffMin}分钟 (持续中)`
    const diffH = (diffMin / 60).toFixed(1)
    return `${diffH}小时 (持续中)`
  }
  const dStart = dayjs(start)
  const dEnd = dayjs(end)
  if (!dStart.isValid() || !dEnd.isValid()) return '--'
  const diffMin = Math.round(dEnd.diff(dStart, 'minute'))
  if (diffMin < 0) return '--'
  if (diffMin < 60) return `${diffMin}分钟`
  const diffH = (diffMin / 60).toFixed(1)
  return `${diffH}小时`
}
</script>

<style scoped lang="scss">
.evidence-box {
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid #f1f5f9;
  background: #f8fafc;

  .evidence-box-title {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;

    .evidence-status-tag {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 4px;
      margin-left: 6px;
      font-weight: 600;

      &.tag-history {
        background: #eff6ff;
        color: #2563eb;
      }
    }
  }

  &.evidence-history {
    background: transparent;
    border: none;
    padding: 0;
  }

  .section-sub-title {
    font-size: 14px !important;
    font-weight: 700 !important;
    color: #1e293b !important;
    line-height: 1.4;
  }

  .history-list {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .history-item {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      font-size: 14px;
      line-height: 1.65;
      color: #334155;

      .history-dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #2563eb;
        margin-top: 8px;
        flex-shrink: 0;
      }
    }
  }

  .embedded-table-section {
    margin-top: 14px;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    overflow: hidden;

    .table-toggle-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 16px;
      background: #f8fafc;
      cursor: pointer;
      user-select: none;
      transition: all 0.2s ease;
      border-bottom: 1px solid transparent;

      &:hover {
        background: #f1f5f9;
      }

      .toggle-bar-left {
        display: flex;
        align-items: center;

        .toggle-title {
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
        }
      }

      .toggle-btn-label {
        font-size: 12px;
        color: #2655f1;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
          opacity: 0.85;
        }
      }
    }

    .table-content-panel {
      border-top: 1px solid #e2e8f0;
      padding: 0;

      .table-scroll-wrap {
        max-height: 300px;
        overflow-y: auto;
        overflow-x: auto;

        &::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        &::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }
      }

      .modern-mini-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
        line-height: 1.5;

        th {
          position: sticky;
          top: 0;
          background: #f8fafc;
          color: #475569;
          font-weight: 600;
          font-size: 12px;
          padding: 6px 10px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
          z-index: 1;
        }

        td {
          padding: 6px 10px;
          color: #1e293b;
          font-size: 12px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;

          &.code-font {
            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
            color: #475569;
            font-size: 11.5px;
          }
        }

        tr:hover td {
          background: #f8faff !important;
        }

        tr.highlight-target-row td {
          background: #eff6ff !important;
          font-weight: 500;
        }

        .dev-name-cell {
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;

          .target-badge {
            display: inline-flex;
            align-items: center;
            font-size: 10px;
            color: #2655f1;
            background: #dbeafe;
            border: 1px solid #bfdbfe;
            padding: 0 5px;
            border-radius: 3px;
            font-weight: 600;
            line-height: 16px;
          }
        }

        .status-pill {
          display: inline-block;
          padding: 1px 7px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
          text-align: center;
          line-height: 16px;
          white-space: nowrap;

          &.pill-recovered {
            background: #f0fdf4;
            color: #16a34a;
            border: 1px solid #bbf7d0;
          }

          &.pill-active {
            background: #fef2f2;
            color: #dc2626;
            border: 1px solid #fecaca;
          }
        }
      }

      .table-empty-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px 16px;
        color: #64748b;
        font-size: 13px;
        background: #ffffff;
      }
    }
  }
}
</style>
